
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Typography,
  Toolbar,
  Box,
  Paper,
  Button,
  TableRow,
  TableBody,
} from "@material-ui/core";
import ReactToPrint from "react-to-print";

import { baseUrl } from "../../../../../config/const";
import Controls from "../../../../../components/controls/Controls";
import useTable from "../../../../../components/useTable";

function formatToIndonesianCurrency(amount) {
  const formatter = new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  });

  return formatter.format(Math.round(Number(amount) || 0));
}

const GajianPabrik = () => {
  const now = new Date();

  const [dateRanges, setDateRanges] = useState([
    new Date(now.getFullYear(), now.getMonth(), 1),
    new Date(now.getFullYear(), now.getMonth() + 1, 0),
  ]);

  const [gajianPabrik, setGajianPabrik] = useState([]);
  const [totalGaji, setTotalGaji] = useState("0");

  const ref = useRef();

  const getGajianPabrik = async () => {
    try {
      const {
        data: { results, totalGaji },
      } = await axios.get(`${baseUrl}/getGajianHarianPabrik`, {
        params: {
          month: dateRanges[0].getMonth() + 1,
          year: dateRanges[0].getFullYear(),
        },
      });

      setGajianPabrik(
        results.rows.map((item, index) => ({
          id: index,
          ...item,
        }))
      );

      setTotalGaji(formatToIndonesianCurrency(totalGaji));
    } catch (err) {
      console.error(err);
    }
  };

  const gajianHeadCells = [
    {
      id: "username",
      disableSorting: true,
      sortable: true,
      flex: 1.5,
      label: "Nama",
    },
    {
      id: "totalabsen",
      disableSorting: true,
      sortable: false,
      flex: 1,
      label: "Tidak Hadir",
      width: 100,
      type: "number",
    },
    {
      id: "totalpresensi",
      disableSorting: true,
      sortable: false,
      flex: 1.5,
      label: "Hadir",
      type: "number",
    },
    {
      id: "totaltidaktercatat",
      disableSorting: true,
      sortable: false,
      flex: 1.5,
      label: "Tidak Checkin",
      type: "number",
    },
    {
      id: "totalmenitterlambat",
      disableSorting: true,
      sortable: false,
      flex: 1.5,
      label: "Terlambat (menit)",
      type: "number",
    },
    {
      id: "totalgaji",
      disableSorting: true,
      sortable: false,
      flex: 1.5,
      label: "Gaji",
      type: "number",
    },
  ];

  // Filter staff pabrik
  const staffPabrik = gajianPabrik.filter(
    (item) =>
      Array.isArray(item.role) &&
      item.role.includes("staffpabrik")
  );

  // Filter staff non-pabrik
  const staffNonPabrik = gajianPabrik.filter(
    (item) =>
      !Array.isArray(item.role) ||
      !item.role.includes("staffpabrik")
  );

  const {
    TblHead: StaffPabrikHead,
    TblContainer: StaffPabrikContainer,
    styledTableCell: StaffPabrikCell,
    recordsAfterPagingAndSorting: staffPabrikRecords,
  } = useTable(
    staffPabrik,
    gajianHeadCells,
    {
      fn: (items) => items,
    },
    true
  );

  const {
    TblHead: StaffNonPabrikHead,
    TblContainer: StaffNonPabrikContainer,
    styledTableCell: StaffNonPabrikCell,
    recordsAfterPagingAndSorting: staffNonPabrikRecords,
  } = useTable(
    staffNonPabrik,
    gajianHeadCells,
    {
      fn: (items) => items,
    },
    true
  );

  useEffect(() => {
    if (dateRanges[0] && dateRanges[1]) {
      getGajianPabrik();
    }
  }, [dateRanges]);

  // Hitung total gaji berdasarkan data tabel
  const calculateTotalGaji = (items) => {
    return items.reduce(
      (total, item) =>
        total + Number(item.totalgaji || 0),
      0
    );
  };

  const renderTable = (
    title,
    TblContainer,
    TblHead,
    TableCell,
    records
  ) => {
    const items = records();
    const total = calculateTotalGaji(items);

    return (
      <Paper style={{ padding: 5, marginBottom: 20 }}>
        <Typography
          variant="h6"
          style={{
            padding: 10,
            fontWeight: "bold",
          }}
        >
          {title}
        </Typography>

        <Box style={{ height: 10 }} />

        <TblContainer>
          <TblHead />

          <TableBody>
            {items.map((item, index) => (
              <TableRow
                key={item.id ?? index}
                hover
                role="checkbox"
                tabIndex={-1}
                style={{ cursor: "pointer" }}
              >
                <TableCell style={{ fontSize: 30 }}>
                  {item.username}
                </TableCell>

                <TableCell style={{ fontSize: 30 }}>
                  {item.totalabsen}
                </TableCell>

                <TableCell style={{ fontSize: 30 }}>
                  {item.totalpresensi}
                </TableCell>

                <TableCell style={{ fontSize: 30 }}>
                  {item.totaltidaktercatat}
                </TableCell>

                <TableCell style={{ fontSize: 30 }}>
                  {item.totalmenitterlambat}
                </TableCell>

                <TableCell style={{ fontSize: 30 }}>
                  {formatToIndonesianCurrency(item.totalgaji)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TblContainer>

        {/* Total gaji per tabel */}
        <Box
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "1em",
            borderTop: "1px solid #ddd",
          }}
        >
          <Typography
            style={{
              fontSize: 30,
              fontWeight: "bold",
            }}
          >
            Total Gaji: {formatToIndonesianCurrency(total)}
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <>
      <Box>
        <Typography variant="h4">
          Gajian Pabrik
        </Typography>

        <Box style={{ marginBottom: 10 }} />

        <Paper style={{ padding: 10 }} elevation={2}>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box style={{ display: "flex", width: "100%" }}>
              <Toolbar style={{ padding: 0 }}>
                <Controls.Label label="Select month: " />

                <Controls.MonthPicker
                  name="month"
                  setDates={setDateRanges}
                  chartPeriod={"monthly"}
                  dateRanges={dateRanges}
                  disableFullMonth={true}
                />
              </Toolbar>

              <Toolbar
                style={{
                  marginLeft: "auto",
                  paddingRight: 0,
                }}
              >
                <ReactToPrint
                  trigger={() => (
                    <Button
                      variant="outlined"
                      color="primary"
                    >
                      Print
                    </Button>
                  )}
                  content={() => ref.current}
                  documentTitle={`pabrik_${
                    dateRanges[0]
                      ? dateRanges[0].toLocaleDateString("en-CA")
                      : ""
                  } - ${
                    dateRanges[1]
                      ? dateRanges[1].toLocaleDateString("en-CA")
                      : ""
                  }`}
                />
              </Toolbar>
            </Box>
          </Box>

          <Box style={{ marginBottom: 10 }} />

          <div ref={ref}>
            <Paper style={{ padding: 5 }}>
              {/* Tabel Staff Pabrik */}
              {renderTable(
                "Staff Pabrik",
                StaffPabrikContainer,
                StaffPabrikHead,
                StaffPabrikCell,
                staffPabrikRecords
              )}

              {/* Tabel Staff Non-Pabrik */}
              {renderTable(
                "Staff Non-Pabrik",
                StaffNonPabrikContainer,
                StaffNonPabrikHead,
                StaffNonPabrikCell,
                staffNonPabrikRecords
              )}

              {/* Total keseluruhan */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "1em",
                  borderTop: "2px solid #333",
                }}
              >
                <span
                  style={{
                    fontSize: 30,
                    fontWeight: "bold",
                  }}
                >
                  Total Keseluruhan: {totalGaji}
                </span>
              </div>
            </Paper>
          </div>
        </Paper>
      </Box>
    </>
  );
};

export default GajianPabrik;
